import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, of, Subject, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import {
  GlobalSearchComponent as TwcGlobalSearchComponent,
  GlobalSearchResult,
  IconManager,
  iconQuote,
  iconShipment,
  PermissionService,
} from '@tiltgroup/web-components';
import {
  LtlQuoteRequest,
  LtlQuoteRequestFilters,
  QueryLtlQuoteRequestsWhereWhereConditions,
  QueryShipmentsWhereWhereConditions,
  Shipment,
  ShipmentFilters,
  SqlOperator,
} from '@graphql-types';
import { Permission } from '@shared/interfaces/permission';
import { FilterKey } from '@services/table-filters.service';
import {
  GlobalSearchRequest,
  GlobalSearchResponse,
  SearchQueriesService,
} from '@services/queries/services/search-queries.service';
import { AppRoutes } from '../../../app-routing';
import { ShipmentSearchOptionComponent } from './components/shipment-search-option/shipment-search-option.component';
import { LtlQuoteSearchOptionComponent } from './components/ltl-quote-search-option/ltl-quote-search-option.component';

const SHIPMENTS_LABEL = 'Shipments';
const QUOTES_LABEL = 'Quotes';

type SearchOption = (Shipment | LtlQuoteRequest) & { url: string };

@Component({
  selector: 'lig-global-search',
  standalone: true,
  imports: [
    TwcGlobalSearchComponent,
    ShipmentSearchOptionComponent,
    LtlQuoteSearchOptionComponent,
  ],
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalSearchComponent implements AfterViewInit {
  private _search = inject(SearchQueriesService);
  private _permission = inject(PermissionService);
  private _router = inject(Router);
  private _iconManager = inject(IconManager);
  private _cdr = inject(ChangeDetectorRef);
  private _destroyRef = inject(DestroyRef);

  protected readonly results = signal<GlobalSearchResult<SearchOption>[]>([]);

  private readonly _shipmentTemplate = viewChild<TemplateRef<unknown>>(
    'shipmentTemplateRef'
  );
  private readonly _quoteTemplate =
    viewChild<TemplateRef<unknown>>('quoteTemplateRef');

  private readonly _canViewShipments = this._permission.checkPermission(
    Permission.VIEW_SHIPMENT
  );
  private readonly _canViewQuotes = this._permission.checkPermission(
    Permission.VIEW_LTL_QUOTE_REQUEST
  );

  private readonly _query$ = new Subject<string>();

  constructor() {
    this._iconManager.addIcons([
      { name: 'shipment', icon: iconShipment },
      { name: 'quote', icon: iconQuote },
    ]);

    // switchMap cancels the in-flight request when a newer query — or a clear —
    // arrives, so a slow earlier response can't overwrite newer results or
    // repopulate the panel after the input is emptied.
    this._query$
      .pipe(
        switchMap((query) =>
          query?.length
            ? this._search
                .getGlobalSearchResults(this._buildRequest(query))
                .pipe(map((res) => ({ res, query })))
            : of({ res: null, query })
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(({ res, query }) => {
        if (res) {
          this._updateResults(res, query);
        } else {
          this._initResults();
        }
      });
  }

  ngAfterViewInit(): void {
    // The per-entity <ng-template>s are only resolvable after the view inits;
    // build the (empty) sections then refresh once under OnPush.
    this._initResults();
    this._cdr.detectChanges();
  }

  protected onSearchValueChanged(query: string): void {
    this._query$.next(query ?? '');
  }

  private _initResults(): void {
    const sections: GlobalSearchResult<SearchOption>[] = [];

    const shipmentTemplate = this._shipmentTemplate();
    if (this._canViewShipments && shipmentTemplate) {
      sections.push({
        label: SHIPMENTS_LABEL,
        options: [],
        templateRef: shipmentTemplate,
      });
    }

    const quoteTemplate = this._quoteTemplate();
    if (this._canViewQuotes && quoteTemplate) {
      sections.push({
        label: QUOTES_LABEL,
        options: [],
        templateRef: quoteTemplate,
      });
    }

    this.results.set(sections);
  }

  private _updateResults(res: GlobalSearchResponse, query: string): void {
    const sections = this.results().map((section) => {
      if (section.label === SHIPMENTS_LABEL) {
        return {
          ...section,
          options: (res?.shipments?.data ?? []).map((shipment) => ({
            ...shipment,
            url: this._optionUrl(SHIPMENTS_LABEL, shipment.id),
          })),
          url: this._sectionUrl(SHIPMENTS_LABEL, query),
        };
      }

      if (section.label === QUOTES_LABEL) {
        return {
          ...section,
          options: (res?.ltlQuoteRequests?.data ?? []).map((quote) => ({
            ...quote,
            url: this._optionUrl(QUOTES_LABEL, quote.id),
          })),
          url: this._sectionUrl(QUOTES_LABEL, query),
        };
      }

      return section;
    });

    this.results.set(sections);
  }

  /** Deep link to a single result. Shipment → primary outlet; quote → side outlet. */
  private _optionUrl(label: string, id: number): string {
    if (label === QUOTES_LABEL) {
      return this._router.serializeUrl(
        this._router.createUrlTree([
          {
            outlets: {
              primary: AppRoutes.QUOTES,
              side: [AppRoutes.QUOTES, id],
            },
          },
        ])
      );
    }

    return this._router.serializeUrl(
      this._router.createUrlTree(['/', AppRoutes.SHIPMENTS, id])
    );
  }

  /** "Show more" link to the list page, pre-filtered by the query on ID. */
  /**
   * "Show more" link to the list page. The list filters one column at a time
   * (AND-combined), so it can't express the multi-field OR the search uses. A
   * numeric query maps cleanly to the ID filter; for free-text we link to the
   * unfiltered list rather than an ID filter that would return nothing.
   */
  private _sectionUrl(label: string, query: string): string {
    const path =
      label === QUOTES_LABEL ? AppRoutes.QUOTES : AppRoutes.SHIPMENTS;

    const tree = this._isNumeric(query)
      ? this._router.createUrlTree(['/', path], {
          queryParams: {
            [`${FilterKey.ID}-input`]: query.trim(),
            [`${FilterKey.ID}-logical`]: SqlOperator.Eq,
          },
        })
      : this._router.createUrlTree(['/', path]);

    return this._router.serializeUrl(tree);
  }

  private _buildRequest(query: string): GlobalSearchRequest {
    return {
      page: 1,
      size: 20,
      canQueryShipment: this._canViewShipments,
      canQueryQuote: this._canViewQuotes,
      whereShipments: this._canViewShipments
        ? this._buildShipmentWhere(query)
        : undefined,
      whereQuotes: this._canViewQuotes
        ? this._buildQuoteWhere(query)
        : undefined,
    };
  }

  private _buildShipmentWhere(
    query: string
  ): QueryShipmentsWhereWhereConditions {
    const value = `%${query}%`;
    const or: QueryShipmentsWhereWhereConditions[] = [
      {
        column: ShipmentFilters.ConcatenatedReferences,
        operator: SqlOperator.Ilike,
        value,
      },
    ];

    if (this._isNumeric(query)) {
      or.unshift({
        column: ShipmentFilters.Id,
        operator: SqlOperator.Eq,
        value: query.trim(),
      });
    }

    return { OR: or };
  }

  private _buildQuoteWhere(
    query: string
  ): QueryLtlQuoteRequestsWhereWhereConditions {
    const value = `%${query}%`;
    const or: QueryLtlQuoteRequestsWhereWhereConditions[] = [
      {
        column: LtlQuoteRequestFilters.QuoteNumber,
        operator: SqlOperator.Ilike,
        value,
      },
      {
        column: LtlQuoteRequestFilters.BolNumber,
        operator: SqlOperator.Ilike,
        value,
      },
      {
        column: LtlQuoteRequestFilters.ProNumber,
        operator: SqlOperator.Ilike,
        value,
      },
      {
        column: LtlQuoteRequestFilters.PickupNumber,
        operator: SqlOperator.Ilike,
        value,
      },
      {
        column: LtlQuoteRequestFilters.OriginPlace,
        operator: SqlOperator.Ilike,
        value,
      },
      {
        column: LtlQuoteRequestFilters.DestinationPlace,
        operator: SqlOperator.Ilike,
        value,
      },
    ];

    if (this._isNumeric(query)) {
      or.unshift({
        column: LtlQuoteRequestFilters.Id,
        operator: SqlOperator.Eq,
        value: query.trim(),
      });
    }

    return { OR: or };
  }

  private _isNumeric(query: string): boolean {
    return /^\d+$/.test(query.trim());
  }
}
